/**
 * 💾 BACKUP SERVICE — Organization Data Export & Restore
 *
 * Exports all organization data as structured JSON for portability (LGPD Art. 18).
 * Components must use this service — never query backup data directly.
 */

import { supabase } from '../lib/supabaseClient';

// ─── Types ────────────────────────────────────────────────────

export interface BackupManifest {
  version: '1.0';
  product: 'workflow-manager';
  exportedAt: string;
  organizationId: string;
  organizationName: string;
  tables: Record<string, number>; // table name → row count
}

export interface OrganizationBackup {
  manifest: BackupManifest;
  organization: Record<string, unknown>;
  departments: Record<string, unknown>[];
  sectors: Record<string, unknown>[];
  processes: Record<string, unknown>[];
  users: Record<string, unknown>[];
  projects: Record<string, unknown>[];
  workflowTemplates: Record<string, unknown>[];
  workflowStages: Record<string, unknown>[];
  workflowInstances: Record<string, unknown>[];
  workflowTasks: Record<string, unknown>[];
  blueprints: Record<string, unknown>[];
  kaizenRecords: Record<string, unknown>[];
  metricsSnapshots: Record<string, unknown>[];
  actions: Record<string, unknown>[];
  auditLogs: Record<string, unknown>[];
}

// ─── Export ───────────────────────────────────────────────────

/**
 * Exports all organization data as a structured JSON backup.
 * Respects RLS — only exports data the user has access to.
 */
export async function exportOrganizationData(
  organizationId: string
): Promise<OrganizationBackup> {
  // Run all queries in parallel for speed
  const [
    orgResult,
    deptsResult,
    sectorsResult,
    processesResult,
    usersResult,
    projectsResult,
    templatesResult,
    stagesResult,
    instancesResult,
    tasksResult,
    blueprintsResult,
    kaizensResult,
    snapshotsResult,
    actionsResult,
    logsResult,
  ] = await Promise.all([
    supabase.from('organizations').select('*').eq('id', organizationId).single(),
    supabase.from('org_departments').select('*').eq('organization_id', organizationId),
    fetchSectorsForOrg(organizationId),
    fetchProcessesForOrg(organizationId),
    supabase.from('user_organizations').select('user_id, role, created_at').eq('organization_id', organizationId),
    supabase.from('projects').select('*').eq('organization_id', organizationId),
    supabase.from('workflow_templates_v9').select('*').eq('organization_id', organizationId),
    fetchStagesForOrg(organizationId),
    supabase.from('workflow_instances_v9').select('*').eq('organization_id', organizationId),
    supabase.from('workflow_tasks_v9').select('*').eq('organization_id', organizationId),
    supabase.from('process_blueprints').select('*').eq('organization_id', organizationId),
    supabase.from('kaizen_records').select('*').eq('organization_id', organizationId),
    fetchSnapshotsForOrg(organizationId),
    fetchActionsForOrg(organizationId),
    supabase.from('audit_logs').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(1000),
  ]);

  const departments = deptsResult.data ?? [];
  const sectors = sectorsResult;
  const processes = processesResult;
  const users = usersResult.data ?? [];
  const projects = projectsResult.data ?? [];
  const templates = templatesResult.data ?? [];
  const stages = stagesResult;
  const instances = instancesResult.data ?? [];
  const tasks = tasksResult.data ?? [];
  const blueprints = blueprintsResult.data ?? [];
  const kaizens = kaizensResult.data ?? [];
  const snapshots = snapshotsResult;
  const actions = actionsResult;
  const logs = logsResult.data ?? [];

  const backup: OrganizationBackup = {
    manifest: {
      version: '1.0',
      product: 'workflow-manager',
      exportedAt: new Date().toISOString(),
      organizationId,
      organizationName: orgResult.data?.name || '',
      tables: {
        departments: departments.length,
        sectors: sectors.length,
        processes: processes.length,
        users: users.length,
        projects: projects.length,
        workflowTemplates: templates.length,
        workflowStages: stages.length,
        workflowInstances: instances.length,
        workflowTasks: tasks.length,
        blueprints: blueprints.length,
        kaizenRecords: kaizens.length,
        metricsSnapshots: snapshots.length,
        actions: actions.length,
        auditLogs: logs.length,
      },
    },
    organization: orgResult.data ?? {},
    departments,
    sectors,
    processes,
    users,
    projects,
    workflowTemplates: templates,
    workflowStages: stages,
    workflowInstances: instances,
    workflowTasks: tasks,
    blueprints,
    kaizenRecords: kaizens,
    metricsSnapshots: snapshots,
    actions,
    auditLogs: logs,
  };

  return backup;
}

/**
 * Downloads the backup as a JSON file.
 */
export function downloadBackup(backup: OrganizationBackup): void {
  const json = JSON.stringify(backup, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const orgName = backup.manifest.organizationName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-');
  const date = new Date().toISOString().slice(0, 10);
  const filename = `backup-${orgName}-${date}.json`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Exports specific table data as CSV.
 */
export function exportTableCsv(
  data: Record<string, unknown>[],
  filename: string
): void {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row =>
      headers.map(h => {
        const val = row[h];
        const str = val === null || val === undefined ? '' : String(val);
        // Escape CSV special characters
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    ),
  ];

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Internal Helpers ─────────────────────────────────────────

async function fetchSectorsForOrg(orgId: string): Promise<Record<string, unknown>[]> {
  const { data: depts } = await supabase
    .from('org_departments')
    .select('id')
    .eq('organization_id', orgId);

  if (!depts?.length) return [];

  const deptIds = depts.map(d => d.id);
  const { data } = await supabase
    .from('org_sectors')
    .select('*')
    .in('department_id', deptIds);

  return data ?? [];
}

async function fetchProcessesForOrg(orgId: string): Promise<Record<string, unknown>[]> {
  const sectors = await fetchSectorsForOrg(orgId);
  if (!sectors.length) return [];

  const sectorIds = sectors.map(s => (s as { id: string }).id);
  const { data } = await supabase
    .from('org_processes')
    .select('*')
    .in('sector_id', sectorIds);

  return data ?? [];
}

async function fetchStagesForOrg(orgId: string): Promise<Record<string, unknown>[]> {
  const { data: templates } = await supabase
    .from('workflow_templates_v9')
    .select('id')
    .eq('organization_id', orgId);

  if (!templates?.length) return [];

  const templateIds = templates.map(t => t.id);
  const { data } = await supabase
    .from('workflow_stages_v9')
    .select('*')
    .in('template_id', templateIds);

  return data ?? [];
}

async function fetchSnapshotsForOrg(orgId: string): Promise<Record<string, unknown>[]> {
  const { data: kaizens } = await supabase
    .from('kaizen_records')
    .select('id')
    .eq('organization_id', orgId);

  if (!kaizens?.length) return [];

  const kaizenIds = kaizens.map(k => k.id);
  const { data } = await supabase
    .from('metrics_snapshots')
    .select('*')
    .in('kaizen_id', kaizenIds);

  return data ?? [];
}

async function fetchActionsForOrg(orgId: string): Promise<Record<string, unknown>[]> {
  const { data: projects } = await supabase
    .from('projects')
    .select('id')
    .eq('organization_id', orgId);

  if (!projects?.length) return [];

  const projectIds = projects.map(p => p.id);
  const { data } = await supabase
    .from('actions')
    .select('*')
    .in('project_id', projectIds);

  return (data ?? []) as Record<string, unknown>[];
}
